import { Action, createReducer, on } from '@ngrx/store';
import {
  activateActions,
  changePasswordActions,
  handleIncorrectPortal,
  impersonate,
  localStorageActions,
  loginActions,
  logoutActions,
  messageHandlingActions,
  registerActions,
  requestPasswordActions,
  resendCodeActions,
  resetPasswordActions,
  setupMFAActions,
  smsChallengeActions,
  ssoActions,
  invitationActions,
  userSessionActions,
} from '../actions/auth.actions';
import { AcceptInvitationResponse, Credentials, LoginResponse } from '../../models';
import { SsoLogin } from '../../models/sso-login.model';

export interface AuthenticateState {
  isLoading: boolean;
  hasLoadTokenFromLocaleStorageActionCompleted: boolean;
  error: string | null;
  showSuccessMessage: boolean;
  token: string | null;
  credentials: Credentials | null;
  loginResponse: LoginResponse | null;
  ssoLogin: SsoLogin | null;
  acceptInvitationResponse: AcceptInvitationResponse | null;}

export const authenticateInitialState: AuthenticateState = {
  isLoading: false,
  hasLoadTokenFromLocaleStorageActionCompleted: false,
  error: null,
  showSuccessMessage: false,
  token: null,
  credentials: null,
  loginResponse: null,
  ssoLogin: null,
  acceptInvitationResponse: null,
};

const authReducer = createReducer(
  authenticateInitialState,

  on(
    loginActions.login,
    registerActions.register,
    activateActions.activate,
    resetPasswordActions.reset,
    requestPasswordActions.request,
    changePasswordActions.change,
    setupMFAActions.confirm,
    ssoActions.loginWithSso,
    invitationActions.validateInvitation,
    invitationActions.acceptInvitation,
    (state) => ({
      ...state,
      isLoading: true,
      showSuccessMessage: false,
      error: null,
    }),
  ),

  on(
    registerActions.succeed,
    activateActions.succeed,
    resetPasswordActions.succeed,
    requestPasswordActions.succeed,
    changePasswordActions.succeed,
    messageHandlingActions.showSuccess,
    ssoActions.loginWithSsoSucceed,
    invitationActions.acceptInvitationSucceed,
    (state) => ({
      ...state,
      isLoading: false,
      showSuccessMessage: true,
      error: null,
    }),
  ),

  on(
    loginActions.failed,
    registerActions.failed,
    activateActions.failed,
    resetPasswordActions.failed,
    requestPasswordActions.failed,
    changePasswordActions.failed,
    resendCodeActions.failed,
    smsChallengeActions.failed,
    setupMFAActions.failed,
    ssoActions.loadSsoLoginFailed,
    ssoActions.loginWithSsoFailed,
    invitationActions.validateInvitationFailed,
    invitationActions.acceptInvitationFailed,
    (state, { error }) => ({
      ...state,
      isLoading: false,
      showSuccessMessage: false,
      error: error.message,
    }),
  ),

  on(messageHandlingActions.hideSuccess, (state, { message }) => ({
    ...state,
    isLoading: false,
    showSuccessMessage: false,
    error: message,
  })),

  on(logoutActions.succeed, logoutActions.authguard, userSessionActions.clearsucceed, () => ({
    ...authenticateInitialState,
  })),

  on(localStorageActions.loadTokenSucceed, (state, { token }) => ({
    ...state,
    hasLoadTokenFromLocaleStorageActionCompleted: true,
    token,
  })),

  on(messageHandlingActions.hideError, smsChallengeActions.confirm, (state) => ({
    ...state,
    error: null,
  })),

  on(impersonate, resendCodeActions.resend, (state) => ({
    ...state,
    isLoading: true,
    showSuccessMessage: false,
    error: null,
  })),

  on(loginActions.succeed, (state, { token }) => ({
    ...state,
    token,
    loginResponse: null,
    credentials: null,
  })),

  on(smsChallengeActions.request, setupMFAActions.request, (state, { credentials, loginResponse }) => ({
    ...state,
    isLoading: false,
    error: null,
    credentials,
    loginResponse,
  })),

  on(resendCodeActions.succeed, (state, { loginResponse }) => ({
    ...state,
    isLoading: false,
    error: null,
    loginResponse,
  })),

  on(smsChallengeActions.reset, (state) => ({
    ...state,
    isLoading: false,
    error: null,
    loginResponse: null,
    credentials: null,
  })),

  on(handleIncorrectPortal, (state) => ({
    ...state,
    token: authenticateInitialState.token,
  })),

  on(ssoActions.loadSsoLogin, (state) => ({
    ...state,
    isLoading: true,
  })),
  on(ssoActions.loadSsoLoginSucceed, (state, { ssoLogin }) => ({
    ...state,
    isLoading: false,
    ssoLogin,
  })),

  on(invitationActions.validateInvitationSucceed, (state, acceptInvitationResponse) => ({
    ...state,
    isLoading: false,
    error: null,
    acceptInvitationResponse
  })),
);

export function authenticateReducer(state = authenticateInitialState, action: Action): AuthenticateState {
  return authReducer(state, action);
}
